import { create } from 'zustand'
import { MenuCategory, MenuItem, CreateMenuCategoryData, UpdateMenuCategoryData, CreateMenuItemData, UpdateMenuItemData } from '../models/menu'
import { menuCategoryService, menuItemService } from '../../core/services'
import toast from 'react-hot-toast'
import i18n from '../i18n'

interface MenuManagementState {
  // Categories
  categories: MenuCategory[]
  selectedCategory: MenuCategory | null
  
  // Items
  items: MenuItem[]
  selectedItem: MenuItem | null
  
  // UI State
  isLoading: boolean
  error: string | null
  currentBusinessId: number | null

  // Category Actions
  fetchCategories: (businessId: number) => Promise<void>
  createCategory: (data: CreateMenuCategoryData) => Promise<void>
  updateCategory: (id: number, data: UpdateMenuCategoryData) => Promise<void>
  deleteCategory: (id: number) => Promise<void>
  toggleCategory: (id: number) => Promise<void>
  setSelectedCategory: (category: MenuCategory | null) => void

  // Item Actions
  fetchItems: (categoryId: number) => Promise<void>
  fetchItemsByBusiness: (businessId: number) => Promise<void>
  createItem: (data: CreateMenuItemData) => Promise<void>
  updateItem: (id: number, data: UpdateMenuItemData) => Promise<void>
  deleteItem: (id: number) => Promise<void>
  toggleItem: (id: number) => Promise<void>
  uploadItemImage: (id: number, file: File) => Promise<void>
  removeItemImage: (id: number) => Promise<void>
  setSelectedItem: (item: MenuItem | null) => void

  // Utility Actions
  setBusinessId: (businessId: number) => void
  clearData: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useMenuManagementStore = create<MenuManagementState>((set) => ({
  // Initial State
  categories: [],
  selectedCategory: null,
  items: [],
  selectedItem: null,
  isLoading: false,
  error: null,
  currentBusinessId: null,

  // Category Actions
  fetchCategories: async (businessId: number) => {
    try {
      set({ isLoading: true, error: null, currentBusinessId: businessId })
      const categories = await menuCategoryService.getCategories(businessId.toString())
      set({ categories })
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch categories'
      set({ error: errorMessage })
      toast.error(errorMessage)
    } finally {
      set({ isLoading: false })
    }
  },

  createCategory: async (data: CreateMenuCategoryData) => {
    try {
      set({ isLoading: true, error: null })
      const newCategory = await menuCategoryService.createCategory(data)
      set(state => ({ 
        categories: [...state.categories, newCategory]
      }))
      toast.success('تم إنشاء الفئة بنجاح')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create category'
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  updateCategory: async (id: number, data: UpdateMenuCategoryData) => {
    try {
      set({ isLoading: true, error: null })
      const updatedCategory = await menuCategoryService.updateCategory(id.toString(), data)
      set(state => ({
        categories: state.categories.map(cat => 
          cat.id === id ? updatedCategory : cat
        ),
        selectedCategory: state.selectedCategory?.id === id ? updatedCategory : state.selectedCategory
      }))
      toast.success('تم تحديث الفئة بنجاح')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update category'
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  deleteCategory: async (id: number) => {
    try {
      set({ isLoading: true, error: null })
      await menuCategoryService.deleteCategory(id.toString())
      set(state => ({
        categories: state.categories.filter(cat => cat.id !== id),
        selectedCategory: state.selectedCategory?.id === id ? null : state.selectedCategory
      }))
      toast.success(i18n.t('categoryDeletedSuccessfully'))
    } catch (error: any) {
      let errorMessage = i18n.t('categoryDeleteError')
      
      // Handle different error types
      if (error.response?.data?.error) {
        const apiError = error.response.data.error
        if (apiError.type === 'NOT_FOUND_ERROR') {
          errorMessage = i18n.t('categoryDeleteNotFound')
        } else if (apiError.type === 'AUTHENTICATION_ERROR') {
          errorMessage = i18n.t('categoryDeleteUnauthorized')
        } else if (apiError.type === 'VALIDATION_ERROR') {
          errorMessage = apiError.message || i18n.t('categoryDeleteError')
        } else if (apiError.message) {
          errorMessage = apiError.message
        }
      } else if (error.message) {
        // Check if message is in English and translate common errors
        const msg = error.message.toLowerCase()
        if (msg.includes('not found')) {
          errorMessage = i18n.t('categoryDeleteNotFound')
        } else if (msg.includes('unauthorized') || msg.includes('permission')) {
          errorMessage = i18n.t('categoryDeleteUnauthorized')
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = i18n.t('categoryDeleteNetworkError')
        } else {
          errorMessage = error.message
        }
      }
      
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  toggleCategory: async (id: number) => {
    try {
      const updatedCategory = await menuCategoryService.toggleCategory(id.toString())
      set(state => ({
        categories: state.categories.map(cat => 
          cat.id === id ? updatedCategory : cat
        ),
        selectedCategory: state.selectedCategory?.id === id ? updatedCategory : state.selectedCategory
      }))
      toast.success(`تم ${updatedCategory.isVisible ? 'تفعيل' : 'إلغاء'} الفئة`)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to toggle category'
      toast.error(errorMessage)
    }
  },

  setSelectedCategory: (category: MenuCategory | null) => {
    set({ selectedCategory: category })
  },

  // Item Actions
  fetchItems: async (categoryId: number) => {
    try {
      set({ isLoading: true, error: null })
      const items = await menuItemService.getItems(categoryId.toString())
      set({ items })
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch items'
      set({ error: errorMessage })
      toast.error(errorMessage)
    } finally {
      set({ isLoading: false })
    }
  },

  fetchItemsByBusiness: async (businessId: number) => {
    try {
      set({ isLoading: true, error: null })
      const items = await menuItemService.getItemsByBusiness(businessId.toString())
      set({ items })
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch items'
      set({ error: errorMessage })
      toast.error(errorMessage)
    } finally {
      set({ isLoading: false })
    }
  },

  createItem: async (data: CreateMenuItemData) => {
    try {
      set({ isLoading: true, error: null })
      const newItem = await menuItemService.createItem(data)
      set(state => ({ 
        items: [...state.items, newItem]
      }))
      toast.success('تم إنشاء الصنف بنجاح')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create item'
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  updateItem: async (id: number, data: UpdateMenuItemData) => {
    try {
      set({ isLoading: true, error: null })
      const updatedItem = await menuItemService.updateItem(id.toString(), data)
      set(state => ({
        items: state.items.map(item => 
          item.id === id ? updatedItem : item
        ),
        selectedItem: state.selectedItem?.id === id ? updatedItem : state.selectedItem
      }))
      toast.success('تم تحديث الصنف بنجاح')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update item'
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  deleteItem: async (id: number) => {
    try {
      set({ isLoading: true, error: null })
      await menuItemService.deleteItem(id.toString())
      set(state => ({
        items: state.items.filter(item => item.id !== id),
        selectedItem: state.selectedItem?.id === id ? null : state.selectedItem
      }))
      toast.success(i18n.t('itemDeletedSuccessfully'))
    } catch (error: any) {
      let errorMessage = i18n.t('itemDeleteError')
      
      // Handle different error types
      if (error.response?.data?.error) {
        const apiError = error.response.data.error
        if (apiError.type === 'NOT_FOUND_ERROR') {
          errorMessage = i18n.t('itemDeleteNotFound')
        } else if (apiError.type === 'AUTHENTICATION_ERROR') {
          errorMessage = i18n.t('itemDeleteUnauthorized')
        } else if (apiError.type === 'VALIDATION_ERROR') {
          errorMessage = apiError.message || i18n.t('itemDeleteError')
        } else if (apiError.message) {
          errorMessage = apiError.message
        }
      } else if (error.message) {
        // Check if message is in English and translate common errors
        const msg = error.message.toLowerCase()
        if (msg.includes('not found')) {
          errorMessage = i18n.t('itemDeleteNotFound')
        } else if (msg.includes('unauthorized') || msg.includes('permission')) {
          errorMessage = i18n.t('itemDeleteUnauthorized')
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = i18n.t('itemDeleteNetworkError')
        } else {
          errorMessage = error.message
        }
      }
      
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  toggleItem: async (id: number) => {
    try {
      const updatedItem = await menuItemService.toggleItem(id.toString())
      set(state => ({
        items: state.items.map(item => 
          item.id === id ? updatedItem : item
        ),
        selectedItem: state.selectedItem?.id === id ? updatedItem : state.selectedItem
      }))
      toast.success(updatedItem.isAvailable ? 'تم تفعيل الصنف' : 'تم إيقاف الصنف')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to toggle item'
      toast.error(errorMessage)
    }
  },

  uploadItemImage: async (id: number, file: File) => {
    try {
      set({ isLoading: true, error: null })
      await menuItemService.uploadItemImage(id.toString(), file)
      toast.success('تم رفع الصورة بنجاح')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to upload image'
      set({ error: errorMessage })
      // Don't show toast here - the response interceptor will handle it
      // toast.error(errorMessage)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  removeItemImage: async (id: number) => {
    try {
      await menuItemService.removeItemImage(id.toString())
      toast.success('تم حذف الصورة بنجاح')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to remove image'
      toast.error(errorMessage)
    }
  },

  setSelectedItem: (item: MenuItem | null) => {
    set({ selectedItem: item })
  },

  // Utility Actions
  setBusinessId: (businessId: number) => {
    set({ currentBusinessId: businessId })
  },

  clearData: () => {
    set({
      categories: [],
      selectedCategory: null,
      items: [],
      selectedItem: null,
      error: null,
      currentBusinessId: null
    })
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),
  setError: (error: string | null) => set({ error })
}))